import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import { Fingerprint } from '@mui/icons-material';
import BiometricService from '../services/BiometricService';
import { setBiometricInProgress } from '../utils/sessionFlags';
import { useTranslation } from 'react-i18next';

interface BiometricLoginProps {
  email: string;
  password: string;
  onBiometricSuccess: (token: string, userData?: any) => void;
  onBiometricError: (error: string) => void;
}

const BiometricLogin: React.FC<BiometricLoginProps> = ({
  email,
  password,
  onBiometricSuccess,
  onBiometricError
}) => {
  const { t } = useTranslation();
  const [hasCredentials, setHasCredentials] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [, setError] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const biometricService = BiometricService.getInstance();

  const checkBiometricCredentials = useCallback(async () => {
    try {
      const hasCreds = await biometricService.hasBiometricCredentials(email);
      setHasCredentials(hasCreds);
    } catch {
      // console.error('Error checking biometric credentials:', error);
    }
  }, [email, biometricService]);

  const handleSetupBiometric = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!password || password.trim() === '') {
        setError('Debe ingresar contraseña para configurar huella digital');
        return;
      }

      await biometricService.registerBiometricCredential(email, password);
      setHasCredentials(true);
    } catch (error: any) {
      const errorMessage = error.message || 'Error configurando la huella digital';
      setError(errorMessage);
      onBiometricError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [email, password, biometricService, onBiometricError]);

  const authInFlightRef = useRef(false);

  const handleBiometricLogin = useCallback(async () => {
    if (!hasCredentials) {
      await handleSetupBiometric();
      return;
    }

    if (authInFlightRef.current) {
      return;
    }

    authInFlightRef.current = true;
    setBiometricInProgress(true);
    setIsLoading(true);
    setError(null);
    setShowCancelDialog(true);

    try {
      const result = await biometricService.authenticateWithBiometric(email);

      if (typeof result === 'string') {
        onBiometricSuccess(result);
      } else {
        onBiometricSuccess(result.token, result.user);
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Error en la autenticación biométrica';
      setError(errorMessage);
      onBiometricError(errorMessage);
    } finally {
      authInFlightRef.current = false;
      setBiometricInProgress(false);
      setIsLoading(false);
      setShowCancelDialog(false);
    }
  }, [
    hasCredentials,
    email,
    biometricService,
    onBiometricSuccess,
    onBiometricError,
    handleSetupBiometric,
  ]);

  const handleBiometricLoginRef = useRef(handleBiometricLogin);
  handleBiometricLoginRef.current = handleBiometricLogin;
  const hasAutoTriggeredRef = useRef(false);

  useEffect(() => {
    hasAutoTriggeredRef.current = false;
    if (email && biometricService.isSupported()) {
      checkBiometricCredentials();
    } else {
      setHasCredentials(false);
    }
  }, [email, biometricService, checkBiometricCredentials]);

  useEffect(() => {
    if (hasCredentials && email && biometricService.isSupported() && !hasAutoTriggeredRef.current) {
      const timer = setTimeout(() => {
        if (hasAutoTriggeredRef.current) {
          return;
        }
        hasAutoTriggeredRef.current = true;
        handleBiometricLoginRef.current();
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [hasCredentials, email, biometricService]);

  const handleCancelBiometric = () => {
    setIsLoading(false);
    setShowCancelDialog(false);
    setError('Autenticación biométrica cancelada por el usuario');
  };

  if (!biometricService.isSupported()) {
    return null; // No mostrar si el navegador no soporta WebAuthn
  }

  return (
    <>
      {/* Botón de huella digital - siempre visible */}
      <Button
        fullWidth
        variant="outlined"
        onClick={handleBiometricLogin}
        disabled={isLoading}
        startIcon={
          isLoading ? (
            <CircularProgress size={20} />
          ) : hasCredentials ? (
            <Fingerprint sx={{ color: '#4caf50' }} />
          ) : (
            <Fingerprint sx={{ color: '#1976d2' }} />
          )
        }
        sx={{
          marginTop: 2,
          height: 40,
          fontSize: 14,
          fontWeight: 'normal',
          color: hasCredentials ? '#4caf50' : '#1976d2',
          borderColor: hasCredentials ? '#4caf50' : '#1976d2',
          textTransform: 'capitalize',
          '&:hover': {
            borderColor: hasCredentials ? '#45a049' : '#1565c0',
            backgroundColor: hasCredentials ? 'rgba(76, 175, 80, 0.04)' : 'rgba(25, 118, 210, 0.04)'
          }
        }}
      >
        {hasCredentials ? t('login.accessWithFingerprint') : t('login.configureFingerprint')}
      </Button>

      {/* Diálogo de cancelación durante autenticación biométrica */}
      <Dialog
        open={showCancelDialog}
        onClose={handleCancelBiometric}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: '#1a1a1a',
            color: '#fff',
            borderRadius: 2,
            margin: 0,
            position: 'fixed',
            top: '20vh',
            left: '50%',
            transform: 'translateX(-50%)',
            maxHeight: '60vh'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Fingerprint sx={{ color: '#4caf50', fontSize: 32 }} />
            <Typography variant="h6" sx={{ color: '#fff' }}>
              {t('login.biometricAuth')}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          <CircularProgress size={40} sx={{ color: '#4caf50', mb: 2 }} />
          <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
            {t('login.placeFinger')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#ccc' }}>
            {t('login.waitingVerification')}
          </Typography>
        </DialogContent>
        
      </Dialog>
    </>
  );
}

export default BiometricLogin;
